// Event polling service for automatic updates (hybrid polling + MQTT fallback)

"use strict";

import { ApiEvents } from "@/api/api-events";
import { Request } from "@asanrom/request-browser";
import { EventData } from "@/api/definitions";

export interface EventPollingConfig {
    intervalMs?: number; // Intervalo de polling en milisegundos (default: 60000 = 1 minuto)
    maxEvents?: number; // Maximo de eventos a obtener (default: 50)
    eventType?: string; // Tipo de evento a filtrar 
    lang?: string; // Locale (Idioma) modificado por el usuario
    onEventsUpdate?: (events: EventData[]) => void;
    onUnreadCountUpdate?: (count: number) => void;
    onError?: (error: any) => void;
    onUnauthorized?: () => void;
}

export class EventPollingService {
    private intervalId: number | null = null;
    private config: Required<EventPollingConfig>;
    private isPolling = false;
    private isPaused = false;
    private mqttMode = false;
    private lastEventCount = 0;

    constructor(config: EventPollingConfig = {}) {
        this.config = {
            intervalMs: config.intervalMs || 60000,
            maxEvents: config.maxEvents || 50,
            eventType: config.eventType || 'notification',
            lang: config.lang || 'en',
            onEventsUpdate: config.onEventsUpdate || (() => {}),
            onUnreadCountUpdate: config.onUnreadCountUpdate || (() => {}),
            onError: config.onError || (() => {}),
            onUnauthorized: config.onUnauthorized || (() => {}),
        };
    }

    /**
     * Inicia el polling para las notificaciones
     */
    public startPolling(): void {
        if (this.isPolling) {
            // Polling ya activo
            return;
        }

        this.isPolling = true;
        
        // Polling inmediatamente
        if (!this.isPaused) {
            this.pollEvents();
        }
        
        // Configurar el intervalo
        this.intervalId = window.setInterval(() => {
            if (!this.isPaused) {
                this.pollEvents();
            }
        }, this.config.intervalMs);
    }

    /**
     * Detiene el polling para las notificaciones
     */
    public stopPolling(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        
        this.isPolling = false;
        // Event polling stopped
    }

    /**
     * Verifica si el polling esta activo
     */
    public isActive(): boolean {
        return this.isPolling && !this.isPaused;
    }

    /**
     * Pausa el polling temporalmente (para modo MQTT)
     */
    public pausePolling(): void {
        this.isPaused = true;
        // Event polling paused (MQTT mode active)
    }

    /**
     * Reanuda el polling (cuando MQTT falla)
     */
    public resumePolling(): void {
        this.isPaused = false;
        // Event polling resumed (MQTT fallback mode)
    }

    /**
     * Activa o desactiva el modo MQTT
     */
    public setMQTTMode(enabled: boolean): void {
        this.mqttMode = enabled;
        if (enabled) {
            // si MQTT esta activo, detener el polling
            this.stopPollingTimer();
            // Event polling timer stopped (MQTT mode active)
        } else {
            // Reiniciar el polling cuando MQTT falla
            this.startPollingTimer();
            // Event polling timer restarted (MQTT fallback mode)
        }
    }

    /**
     * Detiene solo el timer, manteniendo el estado isPolling
     */
    private stopPollingTimer(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isPaused = true;
    }

    /**
     * Reinicia solo el timer si el servicio está activo
     */
    private startPollingTimer(): void {
        if (!this.isPolling) return;
        
        // Limpiar timer existente si lo hay
        this.stopPollingTimer();
        
        this.isPaused = false;
        
        // Configurar nuevo intervalo
        this.intervalId = window.setInterval(() => {
            if (!this.isPaused) {
                this.pollEvents();
            }
        }, this.config.intervalMs);
    }

    /**
     * Actualiza la configuracion del polling
     */
    public updateConfig(newConfig: Partial<EventPollingConfig>): void {
        this.config = { ...this.config, ...newConfig };
        
        // Reinicia el polling si el intervalo cambia y el polling esta activo
        if (newConfig.intervalMs && this.isPolling) {
            this.stopPolling();
            this.startPolling();
        }
    }

    /**
     * Dispara manualmente un polling (util para actualizaciones inmediatas)
     */
    public async pollNow(): Promise<void> {
        if (!this.isPaused) {
            await this.pollEvents();
        }
    }

    /**
     * Metodo de polling interno
     */
    private async pollEvents(): Promise<void> {
        try {
            // Obtiene los eventos y el conteo de eventos no leidos en paralelo
            const [events, unreadCountResponse] = await Promise.all([
                this.fetchEvents(),
                this.fetchUnreadCount()
            ]);

            if (events) {
                this.config.onEventsUpdate(events);
            }

            if (unreadCountResponse) {
                const newCount = unreadCountResponse.count;
                
                // Solo dispara la actualizacion si el conteo cambio
                if (newCount !== this.lastEventCount) {
                    this.config.onUnreadCountUpdate(newCount);
                    this.lastEventCount = newCount;
                }
            }

        } catch (error) {
            // Error durante polling - silencioso en producción
            this.config.onError(error);
        }
    }

    /**
     * Obtiene los eventos desde la API
     */
    private async fetchEvents(): Promise<EventData[] | null> {
        try {
            return new Promise((resolve) => {
                Request.Pending(
                    "event-polling-fetch",
                    ApiEvents.GetUserEvents(0, this.config.maxEvents, this.config.lang, this.config.eventType)
                )
                .onSuccess((events) => {
                    resolve(events || []);
                })
                .onCancel(() => {
                    resolve(null);
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequest: () => {
                            // Bad request cuando se obtienen eventos
                            resolve(null);
                        },
                        unauthorized: () => {
                            this.config.onUnauthorized();
                            this.stopPolling(); // Detiene el polling si no esta autorizado
                            resolve(null);
                        },
                        temporalError: () => {
                            // Error temporal cuando se obtienen eventos
                            resolve(null);
                        },
                    });
                })
                .onUnexpectedError(() => {
                    // Error inesperado cuando se obtienen eventos
                    resolve(null);
                });
            });
        } catch (error) {
            // Error obteniendo eventos
            return null;
        }
    }

    /**
     * Obtiene el conteo de eventos no leidos desde la API
     */
    private async fetchUnreadCount(): Promise<{ count: number } | null> {
        try {
            return new Promise((resolve) => {
                Request.Pending(
                    "event-polling-count",
                    ApiEvents.GetUserUnreadEventCount(this.config.eventType)
                )
                .onSuccess((countResponse) => {
                    resolve(countResponse || { count: 0 });
                })
                .onCancel(() => {
                    resolve(null);
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.config.onUnauthorized();
                            this.stopPolling(); // Detiene el polling si no esta autorizado
                            resolve(null);
                        },
                        temporalError: () => {
                            // Error temporal cuando se obtiene conteo
                            resolve(null);
                        },
                    });
                })
                .onUnexpectedError(() => {
                    // Error inesperado cuando se obtiene conteo
                    resolve(null);
                });
            });
        } catch (error) {
            // Error obteniendo conteo - silencioso en producción
            return null;
        }
    }
}

// Instancia global singleton
let globalPollingService: EventPollingService | null = null;

/**
 * Obtiene o crea el servicio de polling de eventos global
 */
export function getEventPollingService(config?: EventPollingConfig): EventPollingService {
    if (!globalPollingService) {
        globalPollingService = new EventPollingService(config);
    } else if (config) {
        globalPollingService.updateConfig(config);
    }
    
    return globalPollingService;
}

/**
 * Detiene y destruye el servicio de polling de eventos global
 */
export function destroyEventPollingService(): void {
    if (globalPollingService) {
        globalPollingService.stopPolling();
        globalPollingService = null;
    }
}